class Tensor {
    constructor(arr) {
        this.shape = [];
        let cur = arr;
        while (Array.isArray(cur) && cur.length > 0) {
            this.shape.push(cur.length);
            cur = cur[0];
        }

        this.arr = arr;
    }

    static constant(shape, val) {
        let create_tensor = (idx) => {
            if (idx >= shape.length) {
                return val;
            }
            
            let a = [];
            for (let i = 0; i < shape[idx]; i++) {
                a.push(create_tensor(idx + 1));
            }
            return a;
        }
        return new Tensor(create_tensor(0));
    }

    static ones(shape) {
        return Tensor.constant([...shape], 1);
    }
    
    static zeros(shape) {
        return Tensor.constant([...shape], 0);
    }

    static equal_shape(...ts) {
        if (ts.length <= 1) {
            return true
        }

        let s1 = ts[0].shape;
        for (let j = 0; j < ts.length; j++) {
            if (s1.length != ts[j].shape.length) {
                return false;
            }
            for (let i = 0; i < s1.length; i++) {
                if (s1[i] != ts[j].shape[i]) {
                    return false;
                }
            }
        }
        return true;
    }

    static mmult(m1, m2) {
        if (m1.shape.length != 2 && m2.shape.length != 2) {
            throw new Error("Non-2 dimensional input given to matrix multiply")
        }
        if (m1.shape[1] != m2.shape[0]) {
            throw new Error(`Matrix dimensions invalid, got ${m1.shape} and ${m2.shape}`)
        }

        let res = [];
        for (let i = 0; i < m1.shape[0]; i++) {
            let a = [];
            for (let j = 0; j < m2.shape[1]; j++) {
                a.push(0);
            }
            res.push(a)
        }

        for (let i = 0; i < m1.shape[0]; i++) {
            for(let k = 0; k < m1.shape[1]; k++) {
                for (let j = 0; j < m2.shape[1]; j++) {
                    res[i][j] += m1.arr[i][k] * m2.arr[k][j];
                }
            }
        }
        
        return new Tensor(res);
    }

    static combine(t1, t2, op, res) {
        if (!Tensor.equal_shape(t1, t2)) {
            throw new Error(`Tensors do not have equal shape, ${this.shape} and ${t2.shape}`)
        }
        let do_op = (idx, t1_prev, t2_prev, res_prev) => {
            for (let i = 0; i < t1.shape[idx]; i++) {
                if (idx >= t1.shape.length - 1) {
                    res_prev[i] = op(t1_prev[i], t2_prev[i]);
                }
                else {
                    do_op(idx + 1, t1_prev[i], t2_prev[i], res_prev[i]);
                }
            }
        }
        do_op(0, t1.arr, t2.arr, res.arr);
    }
    
    static add(t1, t2) {
        let res = Tensor.zeros(t1.shape);
        Tensor.combine(t1, t2, (a, b) => a + b, res);
        return res;
    }
    
    static sub(t1, t2) {
        let res = Tensor.zeros(t1.shape);
        Tensor.combine(t1, t2, (a, b) => a - b, res);
        return res;
    }
    
    static mul(t1, t2) {
        let res = Tensor.zeros(t1.shape);
        Tensor.combine(t1, t2, (a, b) => a * b, res);
        return res;
    }
    
    static div(t1, t2) {
        let res = Tensor.zeros(t1.shape);
        Tensor.combine(t1, t2, (a, b) => a / b, res);
        return res;
    }
    
    static vectorize(t, op) {
        let res = t.copy();
        res.vectorize_(op);
        return res;
    }

    static stack(ts) {
        if (!Tensor.equal_shape(...ts)) {
            throw new Error("Tensors must have all the same shape to be stacked");
        }

        let res_arr = [];
        for (let i = 0; i < ts.length; i++) {
            res_arr.push( JSON.parse(JSON.stringify(ts[i].arr)) ); // deep copy
        }
        return new Tensor(res_arr);
    }

    static cat(ts, dim) {
        if (ts.length == 0) {
            throw new Error("Tensor.cat expects more than one vector");
        }
        if (ts.length == 1) {
            return ts[0].copy();
        }

        if (dim < 0) {
            dim += ts[0].shape.length
        }
        if (dim < 0) {
            throw new Error(`Invalid dimension ${dim} for tensor shape ${ts[0].shape}`)
        }

        // Check if every dimension matches except the one we cat
        let dim_match = true;
        let new_dim = ts[0].shape[dim];
        let s1 = ts[0].shape;
        for (let j = 1; j < ts.length; j++) {
            if (s1.length != ts[j].shape.length) {
                dim_match = false;
            }
            for (let i = 0; i < s1.length; i++) {
                if (i == dim) {
                    new_dim += ts[j].shape[i];
                }
                else if (s1[i] != ts[j].shape[i]) {
                    dim_match = false;
                }
            }
        }
        if (!dim_match) {
            throw new Error("Tensor.cat expects that each tensor matches in" +
                `every dimension except the one specified, got ${ts.map(x => x.shape.toString() + " ")}`);
        }

        let res_dim = [...ts[0].shape];
        res_dim[dim] = new_dim;
        let res = Tensor.zeros(res_dim);

        let assign = (a, b, offset, idx, a_shape) => {
            for (let i = 0; i < a_shape[idx]; i++) {
                let j = (dim == idx) ? i + offset : i;
                if (idx >= a_shape.length - 1) {
                    b[j] = a[i];
                }
                else {
                    assign(a[i], b[j], offset, idx + 1, a_shape);
                }
            }
        }

        let offset = 0;
        for (let t of ts) {
            assign(t.arr, res.arr, offset, 0, t.shape);
            offset += t.shape[dim];
        }
        return res;
    }
    
    vectorize_(op) {
        let do_op = (idx, prev) => {
            for (let i = 0; i < this.shape[idx]; i++) {
                if (idx >= this.shape.length - 1) {
                    prev[i] = op(prev[i]);
                }
                else {
                    do_op(idx + 1, prev[i]);
                }
            }
        }
        do_op(0, this.arr);
    }

    zero_() {
        let zero = (arr) => {
            for (let i = 0; i < arr.length; i++) {
                if (Array.isArray(arr[i])) {
                    zero(arr[i]);
                }
                else {
                    arr[i] = 0;
                }
            }
        }
        zero(this.arr);
    }

    add_(t2) {
        Tensor.combine(this, t2, (a, b) => a + b, this);
        return this;
    }
    
    sub_(t2) {
        Tensor.combine(this, t2, (a, b) => a - b, this);
        return this;
    }
    
    mul_(t2) {
        Tensor.combine(this, t2, (a, b) => a * b, this);
        return this;
    }
    
    div_(t2) {
        Tensor.combine(this, t2, (a, b) => a / b, this);
        return this;
    }

    copy() {
        let res = Tensor.zeros(this.shape);
        let do_op = (idx, prev, res_prev) => {
            for (let i = 0; i < this.shape[idx]; i++) {
                if (idx >= this.shape.length - 1) {
                    res_prev[i] = prev[i];
                }
                else {
                    do_op(idx + 1, prev[i], res_prev[i]);
                }
            }
        }
        do_op(0, this.arr, res.arr);
        return res;
    }

    toString() {
        let get_string = (idx, prev) => {
            if (idx >= this.shape.length - 1) {
                return `[${prev.toString()}]`
            }
    
            let s = ""
            for (let i = 0; i < this.shape[idx]; i++) {
                s += get_string(idx + 1, prev[i])
                if (i < this.shape[idx] - 1) {
                    s += ", "
                }
            }
            return `[${s}]`;
        }
        return get_string(0, this.arr);
    }

    get(shape) {
        let cur = this.arr;
        for (let idx of shape)
            cur = cur[idx];
        return cur;
    }
    
    set(shape, val) {
        if (shape.length != this.shape.length)
            throw new Error(`Invalid index ${shape} on tensor of shape ${this.shape}`);

        let s = (arr, idx) => {
            if (idx >= shape.length - 1 && shape[idx] === undefined) {
                for (let i = 0; i < this.shape[idx]; i++) {
                    arr[i] = val;
                }
            }
            else if (idx >= shape.length - 1) {
                arr[shape[idx]] = val;
            }
            else if (shape[idx] === undefined) {
                for (let i = 0; i < this.shape[idx]; i++) {
                    s(arr[i], idx + 1);
                }
            }
            else {
                s(arr[shape[idx]], idx + 1);
            }
        }
        s(this.arr, 0);
        return this;
    }

    index(i) {
        return new Tensor(this.arr[i]);
    }
}

class Linear {
    constructor(in_features, out_features) {
        // Input is a matrix of size (m, n_x)
        // where m is number of instances,
        //     n_x is number of input features
        // Output is a matrix of size (m, n_y)
        this.n_x = in_features;
        this.n_y = out_features;
        this.weights = Tensor.zeros([this.n_x, this.n_y]);
        this.biases = Tensor.zeros([1, this.n_y]);
    }

    forward(x) {
        if (x.shape.length != 2) {
            throw new Error("Expected 2-dimensional tensor");
        }
        if (x.shape[1] != this.n_x) {
            throw new Error(`Incompatible matrix of shape ${x.shape} given, expected (any, ${this.n_x})`);
        }

        let res = Tensor.mmult(x, this.weights);
        for (let m = 0; m < x.shape[0]; m++) {
            for (let i = 0; i < this.n_y; i++) {
                res.arr[m][i] += this.biases.arr[0][i];
            }
        }
        return res;
    }
}

function tanh(tensor) {
    return Tensor.vectorize(tensor, a => Math.tanh(a));
}

function sigmoid(tensor) {
    return Tensor.vectorize(tensor, a => 1 / (1 + Math.exp(-a)));
}

function softmax(tensor, dim) {
    dim += (dim < 0) ? tensor.shape.length : 0; 
    let res = tensor.copy();
    let comb = [];
    let max_comb = [];
    for (let i = 0; i < tensor.shape.length - 1; i++)
        comb.push(0);
    for (let i = 0; i < tensor.shape.length; i++)
        if (i != dim)
            max_comb.push(tensor.shape[i]);
    
    let slide = (idx) => {
        if (idx < 0)
            return false;
        if (comb[idx] + 1 >= max_comb[idx]) {
            comb[idx] = 0;
            slide(idx - 1);
        }
        else {
            comb[idx] += 1;
            return true;
        }
    }
    
    let yes = true;
    while (yes) {
        let c = [...comb];
        c.splice(dim, 0, 0);
        let total = 0;
        for (let j = 0; j < tensor.shape[dim]; j++) {
            total += Math.exp(tensor.get(c));
            c[dim] += 1;
        }

        c[dim] = 0
        for (let j = 0; j < tensor.shape[dim]; j++) {
            res.set(c, Math.exp(tensor.get(c)) / total);
            c[dim] += 1;
        }

        if (!slide(comb.length - 1))
            break;
    }

    return res;
}

function random_idx(probabilites) {
    if (probabilites.length <= 1)
        return 0;

    let d = {};
    for (let i = 0; i < probabilites.length; i++) {
        d[probabilites[i]] = i;
    }
    probabilites.sort((a, b) => b - a);
    
    let total = [probabilites[0]];
    for (let i = 1; i < probabilites.length; i++) {
        total.push(probabilites[i] + total[total.length - 1]);
    }
    
    let random = Math.random();
    for (let i = 0; i < total.length; i++) {
        if (random <= total[i]) {
            return d[probabilites[i]];
        }
    }
    return probabilites.length - 1;
}


export {Tensor, Linear, tanh, sigmoid, softmax, random_idx};